import { Play, Pause, Menu, Diamond, Square } from 'lucide-react';
import type { ExperienceElement, ElementKeyframe } from '../types';
import { CustomSelect } from '../CustomSelect';

interface ElementWithKeyframes {
  element: ExperienceElement;
  keyframes: ElementKeyframe[];
}

interface Track {
  id: number;
  title: string;
  duration: string;
  audioUrl: string;
}

interface TimelinePanelProps {
  showKeyframePanel: boolean;
  setShowKeyframePanel: (show: boolean) => void;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  selectedKeyframeTime: number | null;
  setSelectedKeyframeTime: (time: number | null) => void;
  selectedElementId: string | null;
  setSelectedElementId: (id: string | null) => void;
  handleAddKeyframe: (interpolate: boolean) => void;
  handleDeleteKeyframe: (elementId: string, time: number) => void;
  playbackTime: number;
  setPlaybackTime: (time: number) => void;
  timelineDuration: number;
  elementsWithKeyframes: ElementWithKeyframes[];
  previewTrackId: number;
  setPreviewTrackId: (id: number) => void;
  tracks: Track[] | undefined;
}

export function TimelinePanel({
  showKeyframePanel,
  setShowKeyframePanel,
  isPlaying,
  setIsPlaying,
  selectedKeyframeTime,
  setSelectedKeyframeTime,
  selectedElementId,
  setSelectedElementId,
  handleAddKeyframe,
  handleDeleteKeyframe,
  playbackTime,
  setPlaybackTime,
  timelineDuration,
  elementsWithKeyframes,
  previewTrackId,
  setPreviewTrackId,
  tracks,
}: TimelinePanelProps) {
  // Get element type prefix for label
  const getElementLabel = (el: ExperienceElement): string => {
    const prefix = el.type[0].toUpperCase();
    const idSuffix = el.id.slice(-4);
    return `${prefix}:${idSuffix}`;
  };

  const canAddKeyframe = selectedElementId !== null;

  return (
    <div className="eb-timeline">
      <div className="timeline-controls">
        <button
          className={`timeline-btn ${showKeyframePanel ? 'active' : ''}`}
          onClick={() => setShowKeyframePanel(!showKeyframePanel)}
          title="Keyframe Editor"
        >
          <Menu size={16} />
        </button>
        <button
          className="timeline-btn play-btn"
          onClick={() => {
            if (!isPlaying) {
              setSelectedKeyframeTime(null);
            }
            setIsPlaying(!isPlaying);
          }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button
          className={`timeline-btn ${!canAddKeyframe ? 'disabled' : ''}`}
          onClick={() => canAddKeyframe && handleAddKeyframe(false)}
          title={canAddKeyframe ? "Add snap keyframe" : "Select an element first"}
          disabled={!canAddKeyframe}
        >
          <Square size={14} />
        </button>
        <button
          className={`timeline-btn ${!canAddKeyframe ? 'disabled' : ''}`}
          onClick={() => canAddKeyframe && handleAddKeyframe(true)}
          title={canAddKeyframe ? "Add interpolating keyframe" : "Select an element first"}
          disabled={!canAddKeyframe}
        >
          <Diamond size={16} />
        </button>
      </div>

      <div className="timeline-time">
        {playbackTime.toFixed(1)}s
        {selectedKeyframeTime !== null && ` [KF: ${selectedKeyframeTime.toFixed(1)}s]`}
      </div>

      <div className="timeline-scrubber-area">
        {/* Scrubber row */}
        <div className="timeline-scrubber">
          <input
            type="range"
            min="0"
            max={timelineDuration}
            step="0.1"
            value={playbackTime}
            onChange={(e) => {
              const newTime = parseFloat(e.target.value);
              setPlaybackTime(newTime);
              setIsPlaying(false);
              setSelectedKeyframeTime(null);
            }}
            className="timeline-slider"
          />
          <div
            className="timeline-progress"
            style={{ width: `${(playbackTime / timelineDuration) * 100}%` }}
          />
        </div>

        {/* Element rows */}
        {elementsWithKeyframes.map(({ element, keyframes }) => (
          <div
            key={element.id}
            className={`timeline-element-row ${selectedElementId === element.id ? 'selected' : ''}`}
            onClick={() => setSelectedElementId(element.id)}
          >
            <div className="timeline-element-label">
              {getElementLabel(element)}
            </div>
            <div className="timeline-element-keyframes">
              {keyframes.map((kf) => (
                <div
                  key={kf.time}
                  className={`timeline-keyframe-marker ${selectedKeyframeTime === kf.time && selectedElementId === element.id ? 'selected' : ''} ${kf.interpolate ? 'interpolate' : ''}`}
                  style={{ left: `${(kf.time / timelineDuration) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedElementId(element.id);
                    setSelectedKeyframeTime(kf.time);
                    setPlaybackTime(kf.time);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (window.confirm(`Delete keyframe at ${kf.time.toFixed(1)}s?`)) {
                      handleDeleteKeyframe(element.id, kf.time);
                    }
                  }}
                  title={`${kf.time.toFixed(1)}s${kf.interpolate ? ' (interpolating)' : ''}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="timeline-track-select">
        <label>Track:</label>
        <CustomSelect
          value={String(previewTrackId)}
          onChange={(value) => {
            setPreviewTrackId(parseInt(value, 10));
            setSelectedKeyframeTime(null); // Clear on track switch
          }}
          options={
            tracks?.length
              ? tracks.map((t, index) => ({
                  value: String(index + 1),
                  label: `${index + 1}. ${t.title}`,
                }))
              : [{ value: '1', label: 'Track 1' }]
          }
        />
      </div>

      <div className="timeline-duration">
        <span>{Math.floor(timelineDuration / 60)}:{String(Math.round(timelineDuration % 60)).padStart(2, '0')}</span>
      </div>
    </div>
  );
}
